import React, { SyntheticEvent, useEffect, useState } from 'react';
import MapImage from "../components/Map";
import MyTabs from "../components/Tabs";
import CriteriaSelector from '../components/CriteriaSelector';
import { AttributeResponse, BuilderData, BuilderLine, BuilderToCompareData, Comparitor, MessageBuilderData, MessageBuilderToCompareData, MetadataRespone, ResolutionKeys, StateToShow, UserDefinedDataset, WaterTye } from '../types';
import { calculate_expression, get_parameters, handle_attribute_change, handle_dataset_change, handle_page_load, handle_resolution_or_year_or_month_change } from '../functions/backend_handlers';
import MapSelector from '../components/MapSelector';
import ColumnSelector from '../components/ColumnSelector';
import DynamicSelector from '../components/DynamicSelector';
import YearSelectSlider from '../components/Slider';
import ExportButton from '../components/Export';
import WaterTypeRadio from '../components/WaterTypeRadio';
import { water_type_titles, water_type_values } from '../constants';
import useSessionStorage from '../components/Session';
import Alert from '@mui/material/Alert';
import CriteriaComparisonSelector from '../components/CriteriaComparisonSelector';
import CriteriaContinousValueInput from '../components/CriteriaContinousValueInput';
import CriteriaDescreteValueDropdown from '../components/CriteriaDescreteValueDropdown';
import { ActionMeta, SingleValue } from 'react-select';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import MonthSelectSlider from '../components/SliderMonth';
import { hashObject } from '../functions/miscellaneous';

interface DatasetOption {
    value: string;
    label: string;
    category: string;
}

type ComparitorOption = {
    value: Comparitor;
    label: string;
};
  
const discrete_comparitors : ComparitorOption[] = [
    {value: 'keep', label: 'Keep'},
    {value: 'drop', label: 'Drop'}
  ]
  const continous_comparitors : ComparitorOption[] = [
    {value: 'min', label: 'Min'},
    {value: 'max', label: 'Max'},
    {value: 'range', label: 'Range'}
  ]
interface ValidationError {
    field: string;
    line: number;
}


const Builder: React.FC = () => { 

        //user_defined_datasets
        const [available_user_datasets, setAvailableUserDatasets] = useSessionStorage<UserDefinedDataset>("builder_available_user_datasets",{});
        const [current_user_dataset, setCurrentUserDataset] = useSessionStorage<string | undefined>("builder_current_user_dataset",undefined);
        const [current_available_resolitions, setCurrentAvailableResolitions] = useSessionStorage<ResolutionKeys[]>("builder_current_available_resolitions",["state", "county", "huc2", "huc4", "huc6", "huc8"]);
        const [current_user_polygon_values, setCurrentUserPolygonValues] = useSessionStorage<{ [key: string]: string; }>("builder_current_user_polygon_values",{});
        const [is_current_user_defined, setIsCurrentUserDefined] = useSessionStorage<boolean>("builder_is_current_user_defined",false);

        //secondary states
        const [has_time, setHasTime] = useState<boolean>(false);
        const [valid_years, setValidYears] = useState<number[]>([]);
        const [valid_months, setValidMonths] = useState<number[]>([]);
        const [attributes_list, setAttributesList] = useState<{names:string[]; titles: string[]}>({names:[], titles:[]});
        const [available_datasets, setAvailableDatasets] = useSessionStorage<{ categories: string[], names: string[]; titles: string[] }>("builder_available_datasets", {categories:[], names: [], titles: [] });
        const [polygon_values, setPolygonValues] = useState<{ [key: string]: string; }>({});
        const [polygon_type, setPolygonType] = useSessionStorage<"Continuous" | "Discrete">("builder_polygon_type","Continuous");
        const [available_water_types, setAvailableWaterTypes] = useState<WaterTye[]>([undefined]);
        const [current_dataset_level, setCurrentDatasetLevel] = useState< "1" | "2" | "3" | null>(null);
        const [attribute_categories, setAttributeCategories] = useState<{ [key: string]: number }>({});

        // popups on map
        const [attribute_legend_labels, setAttributeLegendLabels] = useState<string>("");

        //main states
        const [current_resolution, setCurrentResolution] = useSessionStorage<ResolutionKeys>("builder_resolution","state");
        
        const [current_dataset, setCurrentDataset] = useState<string | undefined>(undefined);
        const [current_attribute, setCurrentAttribute] = useState<string | undefined>(undefined);
        const [current_year, setCurrentYear] = useState<number | undefined>(0);
        const [current_month, setCurrentMonth] = useState<number | undefined>(0); //
        const [current_parameters, setCurrentParameters] = useState<StateToShow>({})
        const [current_water_type, setCurrentWaterType] = useState<WaterTye>(undefined);

        //Pretty text for main states
        const [current_title_dataset, setCurrentTitleDataset] = useSessionStorage<string>("builder_current_title_dataset","");
        const [current_title_attribute, setCurrentTitleAttribute] = useSessionStorage<string>("builder_current_title_attribute","");

        //builder specific
        const [builderData, setBuilderData] = useSessionStorage<BuilderData>("builder_builderData",[]);
        const [current_selected_index, setCurrentSelectedIndex] = useSessionStorage<number | null>("builder_current_selected_index",null);
        const [current_comparison, setCurrentComparison] = useState<Comparitor | null>(null);
        const [input_values, setInputValues] = useState<(number | undefined)[] | number | string[] | undefined>(undefined);
        const [valid_calculate, setValidCalculate] = useSessionStorage<boolean>("builder_valid_calculate",false);

        const [error, setError] = useState<React.ReactNode>(null);

        const [isExpanded, setIsExpanded] = useState(true);


        const [data_is_missing, setDataIsMissing] = useState(false);

        const [zoomLevel, setZoomLevel] = useSessionStorage<number>("builder_zoomLevel",3.2)
        const [center, setCenter] = useSessionStorage<[number, number]>('builder_center', [38.5, -99.3]);

        
        const toggleExpand = () => {
            setIsExpanded(!isExpanded);
        };
    
        const updateMetadata = (metadata: MetadataRespone) => {
            setAttributesList({names:metadata.Data.available_attributes, titles:metadata.Data.available_attribute_titles});
            setValidYears(metadata.Data.available_years.map( value => Number(value) ));
            setHasTime(metadata.Data.has_time);
            setCurrentDataset(metadata.Data.dataset_name);
            setCurrentYear(Number(metadata.Data.available_years[0]));
            setAttributeLegendLabels(metadata.Data.attribute_legend_labels);
            setAvailableWaterTypes(metadata.Data.available_water_types);
            setCurrentDatasetLevel(metadata.Data.dataset_level);
            setValidMonths(metadata.Data.available_months.map(value => Number(value)));

            setCurrentUserDataset(undefined);
        };

        const updateToDefault = () => {
            setHasTime(false);
            setAttributesList({names:[], titles:[]});
            setCurrentDataset(undefined);
            setCurrentAttribute(undefined);
            setCurrentParameters([]);
            setCurrentUserDataset(undefined);
            setCurrentComparison(null)
            setInputValues(undefined)
            setValidCalculate(false)
        }

        const findInvalidFields = (
            builderData: BuilderData
        ): ValidationError[] => {
            const errors: ValidationError[] = [];
            
            builderData.forEach((line, index) => {
                if (!line.left) {
                    errors.push({ field: 'left', line: index + 1 });
                }
                if (!line.compare) {
                    errors.push({ field: 'compare', line: index + 1 });
                }
                if (line.right === undefined) {
                    errors.push({ field: 'right', line: index + 1 });
                }
            });
        
            return errors;
        };
        

        const updateSelectors = async ( criteria_data : BuilderToCompareData) => {
            if(!criteria_data.is_user_defined){
                await handleDatasetChange(criteria_data.dataset);
                await handleAttributeChange(criteria_data.attribute_name);
                if(criteria_data.year) setCurrentYear(criteria_data.year);
                if(criteria_data.parameter) setCurrentParameters(criteria_data.parameter);
                setCurrentUserDataset(undefined);
            } else {

                setShowUserDataset(available_user_datasets, criteria_data.dataset)
                setCurrentUserDataset(criteria_data.dataset)
            }
        }
    
        const updateAttribute = (attribute: AttributeResponse) => {
            setCurrentResolution(attribute.Data.resolution);
            setPolygonType(attribute.Data.polygon_type);
            setAttributeCategories(attribute.Data.categories)
            setCurrentAttribute(attribute.Data.attribute_name);
            setCurrentWaterType(attribute.Data.water_type);
            
            setCurrentUserPolygonValues({});
            setIsCurrentUserDefined(false);
        };
    
        const handleDataSetChangeOnDropdown = (option: DatasetOption | null) => {
            if(option){
                if(option.category == "Custom Datasets"){ // user defined
                    setShowUserDataset(available_user_datasets, option.value)
                } else {
                    setCurrentTitleDataset(option.label);
                    handleDatasetChange(option.value)
                }
            }
        };
    
        const handleAttributeChangeOnDropdown  = (option: any) => {
            setCurrentTitleAttribute(option.label);
            handleAttributeChange(option.value)

            //set data correctly
            setInputValues(undefined);
            setCurrentComparison(null);
        };
    
        const handleDatasetChange = async (dataset: string | null) => {
            if(dataset){
                setCurrentDataset(dataset);
                const { metadata, current_parameters } = await handle_dataset_change(dataset, current_resolution, current_water_type);
                setCurrentParameters(current_parameters);
                updateMetadata(metadata);
                if(current_attribute) setCurrentAttribute(undefined);
            }
        };

        const handleAttributeWhenSessionLoad = async (dataset: string | null, attribute: string  | null) => {
            if(dataset){
                setCurrentDataset(dataset);
                const { metadata } = await handle_dataset_change(dataset, current_resolution, current_water_type);
                if(attribute){
                    setCurrentParameters(attribute);
                }
                if(current_attribute) setCurrentAttribute(undefined);
                setAttributesList({names:metadata.Data.available_attributes, titles:metadata.Data.available_attribute_titles});
                setValidYears(metadata.Data.available_years.map( value => Number(value) ));
                setHasTime(metadata.Data.has_time);
                setAttributeLegendLabels(metadata.Data.attribute_legend_labels);
                setAvailableWaterTypes(metadata.Data.available_water_types);
                setCurrentDatasetLevel(metadata.Data.dataset_level);
            }
        };
    
        const handleAttributeChange = async (attribute: string) => {
            if(current_dataset){
                setCurrentAttribute(attribute);
                const { metadata, attribute: attrData } = await handle_attribute_change(current_dataset, attribute, current_resolution, current_water_type);
                updateMetadata(metadata);
                if (attrData){
                    setDataIsMissing(false);
                    updateAttribute(attrData);
                }
                else setDataIsMissing(true);
            } 
        };

        const handleCriteriaChange = async (dataset_name : string, attribute_name : string, year : number | undefined, month : number | undefined, current_parameters : StateToShow) => {
            const { metadata } = await handle_dataset_change(dataset_name, current_resolution, current_water_type);
            updateMetadata(metadata);
            setCurrentParameters(current_parameters);

            if(year) setCurrentYear(Number(year));
            
            const attribute = await handle_resolution_or_year_or_month_change(dataset_name, attribute_name, current_parameters, current_resolution, year, month, current_water_type,"False");

            if (attribute){
                setDataIsMissing(false);
                updateAttribute(attribute);
            }
            else setDataIsMissing(true);

        }
    
        const handleResolutionOrYearOrMonthChange = async ( { resolution, year, month }: { resolution?: ResolutionKeys; year?: number; month?: number } ) => {
            if(resolution) setCurrentResolution(resolution);
            else resolution = current_resolution;
            
            if(year) setCurrentYear(Number(year));
            else year = current_year;

            if (month) setCurrentMonth(Number(month))
            else month = current_month;
            
            if(current_dataset && current_attribute){
                const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, current_parameters, resolution, year, month, current_water_type,"False");
                setCurrentParameters(current_parameters);
                if (attribute){
                    setDataIsMissing(false);
                    updateAttribute(attribute);
                }
                else setDataIsMissing(true);
            }
        };

        // Function to filter out "Custom Datasets" and corresponding entries
        const filterCustomDatasets = (data:  { categories: string[]; names: string[]; titles: string[];} ):  { categories: string[]; names: string[]; titles: string[];} => {
            // Create result arrays
            const filteredCategories: string[] = [];
            const filteredNames: string[] = [];
            const filteredTitles: string[] = [];
            
            // Loop through all entries and only keep non-"Custom Datasets" ones
            for (let i = 0; i < data.categories.length; i++) {
            if (data.categories[i] !== "Custom Datasets") {
                filteredCategories.push(data.categories[i]);
                filteredNames.push(data.names[i]);
                filteredTitles.push(data.titles[i]);
            }
            }
            
            return {
            categories: filteredCategories,
            names: filteredNames,
            titles: filteredTitles
            };
        };
    
        useEffect(() => {
            const storedDatasetsString = localStorage.getItem("user_datasets");
            const storedDatasets = storedDatasetsString ? JSON.parse(storedDatasetsString) : {}
            setAvailableUserDatasets(storedDatasets);

            //LOAD LIST!
            handle_page_load(false).then(({metadata, attribute, current_parameters, datalist}) => {
                
                // Append the keys from storedDatasets to datalist
                Object.keys(storedDatasets).forEach(key => {
                    if(current_available_resolitions.length == 1){
                        if(available_user_datasets[key].resolution == current_resolution){
                            datalist.names.push(key);
                            datalist.titles.push(key); // Using the same key as the title
                            datalist.categories.push("Custom Datasets"); // Fixed category for all
                        }
                    } else {
                        datalist.names.push(key);
                        datalist.titles.push(key); // Using the same key as the title
                        datalist.categories.push("Custom Datasets"); // Fixed category for all
                    }

                });
                setAvailableDatasets(datalist);
            }).catch((error : any) => console.error("Error loading data:", error));

            //SET SELECTED IF WE SELECTED BEFORE
                if(current_selected_index != null && current_selected_index >= 0 && builderData.length > 0){

                    const index = current_selected_index;
                    setCurrentSelectedIndex(current_selected_index === index ? null : index)
                    if(builderData[index].left) {
                        if(builderData[index].left.is_user_defined){
                            setShowUserDataset(available_user_datasets, builderData[index].left.dataset)
                        } else {

                            
                            //SET LIST FIRST
                            handleAttributeWhenSessionLoad(builderData[index].left.dataset, builderData[index].left.attribute_name).then(() => {
                                setCurrentAttribute(undefined)
                                setCurrentAttribute(builderData[index].left?.attribute_name)
                            })

                            const tmp_left = builderData[index].left;
                            handleCriteriaChange(tmp_left.dataset, tmp_left.attribute_name, tmp_left.year, tmp_left.month, tmp_left.parameter)
                        }
                    } else {
                        updateToDefault();
                    }
                    const tmp_comp = builderData[index].compare;
                    const tmp_right = builderData[index].right;
                    
                    setCurrentComparison(tmp_comp);
                    setInputValues(tmp_right);
                    setCurrentSelectedIndex(current_selected_index);

                    
                }

                

                //CALCULATE IF VALID
                if(valid_calculate){
                    const messageData = transformBuilderToMessageData(builderData);
                    if (messageData) {
                        calculate_expression(current_resolution, messageData).then((result) => {
                            if (result) {
                                setPolygonValues({...result, hello_kitty_true: 'true', 'hello_kitty_false': 'false'}); //hack_to show true false always!!!
                                setValidCalculate(true);
                            }
                        }).catch((error) => {
                            console.error(error);
                        })
                    }
                }
            
        }, []);
    
    
        const handleTabChangeOnSelect = async (event: SyntheticEvent, newValue: ResolutionKeys) => {
            handleResolutionOrYearOrMonthChange({resolution: newValue}); 
            if(valid_calculate){
                const messageData = transformBuilderToMessageData(builderData);
                if (messageData) {
                    try {
                        const result = await calculate_expression(newValue, messageData);
                        if (result) {
                            setPolygonValues({...result, hello_kitty_true: 'true', 'hello_kitty_false': 'false'}); //hack_to show true false always!!!
                            setValidCalculate(true);
                        }
                    } catch (error) {
                        console.error(error);
                    }
                }
            }
        };
    
        // Handle the slider change
        const handleYearSliderChange = (event: Event, newValue: number | number[]) => {
            // newValue is the index of the selected year
            handleResolutionOrYearOrMonthChange({year: valid_years[newValue as number]}); 
        };

        const handleMonthSliderChange = (event: Event, newValue: number | number[]) => {
            // newValue is the index of the selected year
            handleResolutionOrYearOrMonthChange({ month: valid_months[newValue as number] });
        };


    
        const handleDynamicSelectorChange = async (key: string, value: string) => {
            // Directly modify the current_parameters object
            const updatedParameters = { ...current_parameters };
    
            if (updatedParameters[key]) {
                updatedParameters[key].current_code = value;
            }
    
            if(current_dataset && current_attribute){
                const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, updatedParameters, current_resolution, current_year, current_month, current_water_type,"False");
                setCurrentParameters(updatedParameters);
                if (attribute){
                    setDataIsMissing(false);
                    updateAttribute(attribute);
                }
                else setDataIsMissing(true);
            }
        };

        const handleWaterTypeChange = async (value: string) => {
            if(!current_dataset) return null;
            if(!current_attribute) return null;
            const updated_water_type = value as WaterTye;
            const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, current_parameters, current_resolution, current_year, current_month, updated_water_type,"False");
            if (attribute){
                setDataIsMissing(false);
                updateAttribute(attribute);
            }
            else setDataIsMissing(true);
        };

        const setShowUserDataset = (user_data: UserDefinedDataset, current_user_data: string | undefined) => {
            if(current_user_data){
                setCurrentUserPolygonValues(user_data[current_user_data].polygon_values) // polygon_values
                setCurrentResolution(user_data[current_user_data].resolution) // resolution
                setPolygonType(user_data[current_user_data].polygon_type) // polygon_type
                setAttributeCategories(user_data[current_user_data].categories) // categories
                setCurrentUserDataset(current_user_data);

                setIsCurrentUserDefined(true);
        
                // Extra stuff
                setHasTime(false);
                setCurrentAttribute(undefined);
                setCurrentDataset(undefined);
                setInputValues([]);
            }
        };

        useEffect(() => {
            for(let index=0; index < builderData.length; index++){
                if(builderData[index].left && builderData[index].left?.is_user_defined){

                    //filter data
                    let datalist = filterCustomDatasets(available_datasets);

                    // Append the keys from storedDatasets to datalist
                    Object.keys(available_user_datasets).forEach(key => {
                        if(available_user_datasets[key].resolution == current_resolution){
                            datalist.names.push(key);
                            datalist.titles.push(key); // Using the same key as the title
                            datalist.categories.push("Custom Datasets"); // Fixed category for all
                        }
                    });

                    setAvailableDatasets(datalist);

                    setCurrentAvailableResolitions([current_resolution]);
                    return
                }
            }

            //set all data
            let datalist = filterCustomDatasets(available_datasets);

            // Append the keys from storedDatasets to datalist
            Object.keys(available_user_datasets).forEach(key => {
                datalist.names.push(key);
                datalist.titles.push(key); // Using the same key as the title
                datalist.categories.push("Custom Datasets"); // Fixed category for all
            });

            setAvailableDatasets(datalist);

            setCurrentAvailableResolitions(["state", "county", "huc2", "huc4", "huc6", "huc8"]);
        }, [builderData]);

    const transformBuilderToMessageData = (
        builderData: BuilderData,
        setError?: (error: React.ReactNode) => void
      ): MessageBuilderData | null => {
          const errors = findInvalidFields(builderData);
          
          if (errors.length > 0) {
              const errorMessage = (
                  <Alert sx={{ mb: 2 }} severity="error">
                          Invalid builder data:<br />
                          {errors.map((error, idx) => (
                              <span key={idx}>
                                  Line {error.line}: {error.field} is empty<br />
                              </span>
                          ))}
                  </Alert>
              );
              if(setError)
              setError(errorMessage);
              return null;
          } 
          if(setError)
          setError(null);
    
          // Convert all valid lines to MessageBuilderData format
          const messageData: MessageBuilderData = builderData.map(line => {
              var tmp_left_data = {...line.left};
              tmp_left_data.parameter = tmp_left_data.parameter ? get_parameters(tmp_left_data.parameter) : undefined;
              return {
              left: tmp_left_data as MessageBuilderToCompareData,
              compare: line.compare as Comparitor,
              right: line.right as number[] | number | string[]
            }
          });
    
          return messageData;
      };

    const handleCalculateClick = async () => {    
        const messageData = transformBuilderToMessageData(builderData, setError);
        if (messageData) {
            try {
                const result = await calculate_expression(current_resolution, messageData);
                if (result) {
                    setPolygonValues({...result, hello_kitty_true: 'true', 'hello_kitty_false': 'false'}); //hack_to show true false always!!!
                    setValidCalculate(true);
                }
            } catch (error) {
                setError(
                    <Alert sx={{ mb: 2 }} severity="error">
                        Error calculating expression: {error instanceof Error ? error.message : 'Unknown error'}
                    </Alert>
                );
            }
        }
      };

      // Handle trash icon click
    const handleTrashClick = () => {
        setValidCalculate(false);
        setBuilderData([]);
        setCurrentComparison(null);
        setInputValues(undefined);
        setCurrentSelectedIndex(null);
        updateToDefault();
    };

    const addEmptyCriteria = () => {
        setValidCalculate(false);
        const newLine : BuilderLine = {
          left: null,
          compare: null,
          right: undefined,
        }
    
        setBuilderData(prevData =>{
                if(prevData.length == 0) setCurrentSelectedIndex(0);
                return [newLine, ...prevData];
            }
            );

        updateToDefault();

        setCurrentSelectedIndex(0);
      }

    const handleComparisonChange = (
        newValue: SingleValue<ComparitorOption>,
        actionMeta: ActionMeta<ComparitorOption>
        ) => {
          if(newValue) setCurrentComparison(newValue.value);
    };

    const handleChangeCriteria = () => {
        if(is_current_user_defined && Object.keys(current_user_polygon_values).length > 0){
          setValidCalculate(false);
          setBuilderData(prevData => {
            return prevData.map((line, index) => {
              if (index === current_selected_index) {
                return {
                  ...line,
                  right: input_values,
                  compare: current_comparison,
                  left: {
                    'attribute_name' : '', 
                    'dataset' : current_title_dataset, 
                    "year": undefined, 
                    "month": undefined, 
                    "parameter": {},
                    "type":  polygon_type,
                    "attribute_title": current_title_attribute,
                    "dataset_title" : current_title_dataset,
                    "water_type" : undefined,
                    "is_user_defined" : true,
                    "polygon_values" : current_user_polygon_values
                  }
                };
              }
              return line;
            });
          });
        } else if (current_selected_index != null && current_attribute && current_dataset) {
          setValidCalculate(false);
          setBuilderData(prevData => {
            return prevData.map((line, index) => {
              if (index === current_selected_index) {
                return {
                  ...line,
                  right: input_values,
                  compare: current_comparison,
                  left: {
                    'attribute_name' : current_attribute, 
                    'dataset' : current_dataset, 
                    "year": current_year, 
                    "month": current_month, 
                    "parameter": current_parameters,
                    "type":  polygon_type,
                    "attribute_title": current_title_attribute,
                    "dataset_title" : current_title_dataset,
                    "water_type" : current_water_type,
                    "is_user_defined" : false, 
                    "polygon_values" : {}
                  }
                };
              }
              return line;
            });
          });
        }
    };

    return (
        <>
            <div className="contact-page-container">
                <div className="contact-page-left">
                    <MyTabs selectedTab={current_resolution} onTabChange={handleTabChangeOnSelect} tabStatus={current_available_resolitions} />
                    <div className="contact-page-map-container">
                        <MapImage resolution={current_resolution} polygon_values={polygon_values}
                            min={undefined} max={undefined} categories={{ 'true': 1, 'false': 0 }}
                            polygon_type={"Discrete"} 
                            attribute_name={"Hello world"}
                            current_dataset={"Hello world"}
                            colormap_bases={""}
                            colormap_types={""}
                            attributes_list={attributes_list}
                            current_parameters={"Builder"}
                            available_datasets={available_datasets}
                            selectedOverlay={'None'}
                            attribute_legend_labels={"Builder"}
                            is_hml={false}
                            is_legend={"ON"}
                            show_coloring={true}
                            is_log={false}
                            is_overlay={"False"}
                            geojson_frame={undefined}
                            popup_name={{}}
                            popup_description={undefined}
                            key={`map-${hashObject(polygon_values)}-${"ON"}`} 

                            zoomLevel = {zoomLevel}
                            setZoomLevel = {setZoomLevel}
                            center = {center}
                            setCenter = {setCenter}
                        />
                    </div>
                </div>
    
                <div className="contact-page-right">

                
                { builderData.length == 0 &&
                    <div className="builder-container">
                        <div className="parameter-addition-button">
                            <button onClick={addEmptyCriteria} className="add-button">Add Criteria</button>
                        </div>
                    </div>
                }

                { builderData.length > 0 &&
                    <div className="header-expandable">
                        <h2 className="builder-heading" style={{top: "-9px", position:"relative"}}>Filter Database</h2>
                        <button 
                            className="expand-toggle-button" 
                            onClick={toggleExpand}
                            aria-label={isExpanded ? "Collapse section" : "Expand section"}
                            >
                            {isExpanded ? (<FaChevronUp /> )  : <FaChevronDown />}
                        </button>
                    </div>
                }
      
                { builderData.length > 0 &&
                <div className={`expandable ${isExpanded ? 'expanded' : 'collapsed'}`}>

        
                        {builderData.length > 0 && (
                            <>
                                <div className="builder-container">                   
                                    <h3 className="builder-section-title">Select Dataset</h3>
                                    <div className="builder-input-container">
                                        <MapSelector 
                                            key={current_dataset} 
                                            data={available_datasets}
                                            onSelect={handleDataSetChangeOnDropdown} 
                                            baseValue={current_dataset ? current_dataset : (current_user_dataset ? current_user_dataset : null)}
                                            is_clearable={false}
                                            available_resolutions={[current_resolution]}
                                        />
                                    </div>
                                </div>
                                
                                {(current_dataset != undefined) && (
                                    <div className="builder-container">
                                        <h3 className="builder-section-title">Select Attribute</h3>
                                        <div className="builder-input-container">
                                            <ColumnSelector 
                                                key={current_attribute} 
                                                options={attributes_list.names.map((name, index) => ({ 
                                                    value: name, 
                                                    label: attributes_list.titles[index] || name
                                                }))} 
                                                onSelect={handleAttributeChangeOnDropdown} 
                                                baseValue={(current_attribute ? {
                                                    label: attributes_list.titles[attributes_list.names.indexOf(current_attribute)], 
                                                    value: current_attribute
                                                } : null)} 
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
        
                        {current_dataset_level == "3" && current_water_type && 
                            <div className="builder-container">
                                <WaterTypeRadio 
                                    available_values={available_water_types} 
                                    key={current_water_type} 
                                    title={"Water Type"} 
                                    type={"radio"} 
                                    values={water_type_values} 
                                    labels={water_type_titles} 
                                    name={current_water_type} 
                                    selectedValue={current_water_type} 
                                    onChange={(value) => value ? handleWaterTypeChange(value) : null} 
                                />
                            </div>
                        }
        
                        {builderData.length > 0 && (current_attribute) && 
                            <div className="builder-container">
                                {Object.keys(current_parameters).map((key) => {
                                    const { type, titles, codes, current_code } = current_parameters[key as keyof typeof current_parameters];
                                    const title = key.split("_")[0];
                                    return (
                                        <div className="builder-container" key={key}>
                                            <h3 className="builder-section-title">{title}</h3>
                                            <DynamicSelector
                                                key={key}
                                                title={""} // Empty title since we're using our custom titles
                                                type={type}
                                                values={codes}
                                                labels={titles}
                                                name={key}
                                                selectedValue={current_code}
                                                onChange={(value) => handleDynamicSelectorChange(key, value)}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        }
        
                        {(builderData.length > 0 && current_attribute && has_time && current_year && 
                            <div className="builder-container">
                                <YearSelectSlider 
                                    key={current_year} 
                                    value={current_year} 
                                    handleYearSliderChange={handleYearSliderChange} 
                                    years={valid_years} 
                                />
                            </div>
                        )}

                        {(builderData.length > 0 && current_attribute && has_time && (current_month !== undefined && !isNaN(current_month))  && valid_months.length > 1 && <MonthSelectSlider key={`MonthSelectSlider_${current_month}`} value={current_month} handleMonthSliderChange={handleMonthSliderChange} months={valid_months} />)}
                        
                            { builderData.length > 0 && current_attribute && current_dataset && (  
                                <div className="builder-container">
                                    <h3 className="builder-section-title">Select Filter Type</h3>
                                    <div className="parameter-addition-button">
                                        <CriteriaComparisonSelector data_options={ polygon_type === "Continuous" ? continous_comparitors :  discrete_comparitors} currently_selected_comparitor={current_comparison} handleComparisonChange={handleComparisonChange}/>
                                    </div>   
                                    {current_comparison && <div className="parameter-addition-button" style={{ position: 'static', zIndex: 9999 }}>
                                        {polygon_type === "Continuous" && <CriteriaContinousValueInput current_comparitor={current_comparison} current_value={input_values} setInputValues={setInputValues}/> }
                                        {polygon_type === "Discrete" && <CriteriaDescreteValueDropdown options={attribute_categories ? Object.keys(attribute_categories) : []} current_value={input_values} setInputValues={setInputValues}/> }
                                    </div>}

                                    {<div className="parameter-addition-button-save" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {data_is_missing && (
                                            <div style={{ 
                                                backgroundColor: '#FFF3E0',
                                                border: '1px solid #FFB74D',
                                                borderRadius: '4px',
                                                padding: '6px 12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                fontSize: '13px',
                                                color: '#E65100'
                                            }}>
                                                <span style={{ fontSize: '14px' }}>⚠️</span>
                                                <span>There are no data available for these inputs!</span>
                                            </div>
                                        )}
                                        <button disabled={data_is_missing} onClick={handleChangeCriteria} className="save-changes-button">Save</button>
                                    </div>}

                                </div> 
                            )}

                    </div>
                    }

                    { builderData.length > 0 &&
                        <div className="criteria-content-wrapper">
                            <div className="criteria-header-container">
                                <h2 className="builder-heading" style={{zIndex: 1, position: "relative"}}>Criteria</h2>
                            </div>
                            <CriteriaSelector 
                                setValidCalculate={setValidCalculate}
                                updateSelectors={updateSelectors}
                                updateToDefault={updateToDefault}
                                handleCriteriaChange={handleCriteriaChange}
                                builderData={builderData}
                                current_selected_index={current_selected_index}
                                setBuilderData={setBuilderData}
                                setCurrentSelectedIndex={setCurrentSelectedIndex}
                                setCurrentComparison={setCurrentComparison}
                                setInputValues={setInputValues}
                                setShowUserDataset={setShowUserDataset}
                                user_data={available_user_datasets} 
                                error={error} />
                        </div>
                    }

                    {builderData.length > 0 && (
                    <div className="builder-fixed-footer">
                        <div className="button-group">
                            <ExportButton 
                                polygon_values={polygon_values}
                                resolution={current_resolution}
                                dataset={"builder"}
                                attribute={"data"}
                                parameters={{}}
                                year={undefined}
                                className="criteria-secondary-button"
                                disabled={!valid_calculate}
                            />
                            <button className="criteria-secondary-button" onClick={handleTrashClick}>Clear All</button>
                            <button className="calculate-button" onClick={addEmptyCriteria}>Add New Criteria</button>
                            <button className="calculate-button" onClick={handleCalculateClick}>Calculate</button>
                        </div>
                    </div>
                    )}
            </div>
        </div>
    </>
    );
};

export default Builder;